import {
  Breadcrumb,
  BreadcrumbItem as FluentBreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
  partitionBreadcrumbItems,
  makeStyles,
  Button,
  Menu,
  MenuItem as FluentMenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  useIsOverflowItemVisible,
  useOverflowMenu,
  Overflow,
  OverflowItem,
  OverflowDivider,
  isTruncatableBreadcrumbContent,
  truncateBreadcrumbLongName,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import { MoreHorizontalRegular, MoreHorizontalFilled, bundleIcon, FluentIcon } from '@fluentui/react-icons';
import type { PartitionBreadcrumbItems } from '@fluentui/react-components';
import { Link, useLocation } from 'react-router-dom';
import { ControlledTooltip } from './ControlledTooltip';
import { useDrop } from 'react-dnd';
import { TItem, TOnMove } from '@modules/items';
import { FC, forwardRef, Fragment, HTMLAttributeAnchorTarget, LegacyRef, ReactNode, useRef } from 'react';

const MoreHorizontal = bundleIcon(MoreHorizontalFilled, MoreHorizontalRegular);

type TRenderItem = {
  id: string;
  icon?: FluentIcon;
  text: string;
  href: string;
  target?: Extract<HTMLAttributeAnchorTarget, '_blank' | '_self'>;
  disabled?: boolean;
  onMove?: TOnMove;
};
export type TRenderItemWithKey = TRenderItem & {
  key: number;
};

const useStyles = makeStyles({
  root: {
    width: '100%',
    overflow: 'hidden',
  },

  menuButton: {
    // same height as the crumbs
    padding: `${tokens.spacingHorizontalXXS} 0`,
    // same bg color as the crumbs
    '&:hover': {
      backgroundColor: `${tokens.colorNeutralBackground3Hover}`,
    },
    '&:active': {
      backgroundColor: `${tokens.colorNeutralBackground3Pressed}`,
    },
  },

  // following the OneDrive design
  crumbBase: {
    padding: `0 ${tokens.spacingHorizontalXS} ${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    height: 'fit-content',
  },
  // only applicable to the 'large' Breadcrumb size
  crumbHuge: {
    fontSize: '18px',
  },
  // similar to the OneDrive design
  crumbWithBg: {
    '&:hover': {
      backgroundColor: `${tokens.colorNeutralBackground3Hover}`,
      color: `${tokens.colorNeutralForeground2Hover}`,
    },

    '&:active': {
      backgroundColor: `${tokens.colorNeutralBackground3Pressed}`,
      // color: `${tokens.colorNeutralForeground2Pressed}`,
    },
  },
});

type TDynamicLinkProps = {
  children: ReactNode;
  to?: string;
  target?: Extract<HTMLAttributeAnchorTarget, '_blank' | '_self'>;
  onClick?: () => void;
};
const DynamicLink = forwardRef<HTMLElement, TDynamicLinkProps>(
  ({ to, target, children, onClick }: TDynamicLinkProps, ref) => {
    if (onClick)
      return (
        <span onClick={onClick} ref={ref}>
          {children}
        </span>
      );
    if (to)
      return (
        <span ref={ref}>
          <Link to={to} target={target}>
            {children}
          </Link>
        </span>
      );
    return <span ref={ref}>{children}</span>;
  }
);

const PathMenuItem: FC<{ id: string; item: TRenderItemWithKey; onClick?: (key: number) => void }> = ({
  item,
  id,
  onClick,
}) => {
  const isVisible = useIsOverflowItemVisible(id);
  if (isVisible) {
    return null;
  }

  return (
    <DynamicLink to={item.href} target={item.target} onClick={onClick && (() => onClick(item.key))}>
      <FluentMenuItem>{item.text}</FluentMenuItem>
    </DynamicLink>
  );
};

const OverflowGroupDivider: FC<{
  groupId: number;
}> = (props) => {
  return (
    <OverflowDivider groupId={props.groupId.toString()}>
      <BreadcrumbDivider data-group={props.groupId} className="mt-0.5" />
    </OverflowDivider>
  );
};

type TBreadcrumbItemProps = {
  size?: 'small' | 'medium' | 'large';
  el: TRenderItemWithKey;
  isFirstItem: boolean;
  isLastItem: boolean;
  hasCurrent: boolean;
  onClick?: (key: number) => void;
};
const BreadcrumbItem = ({ size, el, isFirstItem, isLastItem, hasCurrent, onClick }: TBreadcrumbItemProps) => {
  const isCurrent = hasCurrent && isLastItem;

  let isOver = false;
  let canDrop = false;
  let drop = (x: any) => x;

  const location = useLocation();
  if (location.pathname.includes('/folder')) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [{ isOver: isOver2, canDrop: canDrop_ }, drop_] = useDrop({
      // TODO: jan
      accept: 'item',
      drop: (dropItem: TItem) => {
        el.onMove?.(dropItem._id, dropItem.type, el.id);
      },
      canDrop: (dropItem) => Boolean(el.onMove),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    });
    drop = drop_;
    isOver = isOver2;
    canDrop = canDrop_;
  }

  const ref = useRef<HTMLButtonElement>();

  const classes = useStyles();

  return (
    <Fragment key={`button-items-${el.key}`}>
      <OverflowItem id={el.key.toString()} priority={isFirstItem ? 100 : el.key} groupId={el.key.toString()}>
        <FluentBreadcrumbItem>
          <ControlledTooltip
            enable={isTruncatableBreadcrumbContent(el.text, 30)}
            withArrow
            content={el.text}
            relationship="label"
          >
            <DynamicLink
              to={isCurrent ? undefined : el.href}
              target={el.target}
              onClick={onClick && (() => onClick(el.key))}
            >
              <span
                ref={drop(ref) as LegacyRef<HTMLButtonElement>}
                style={{
                  backgroundColor: isOver ? '#f2f2f2' : 'transparent',
                  border: isOver && canDrop ? `2px dashed ${tokens.colorBrandStroke1}` : '2px solid transparent',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}
              >
                <BreadcrumbButton
                  current={isCurrent}
                  aria-disabled={el.disabled}
                  className={mergeClasses(
                    classes.crumbBase,
                    !isCurrent && classes.crumbWithBg,
                    size === 'large' && classes.crumbHuge
                  )}
                  icon={el.icon && <el.icon />}
                >
                  {truncateBreadcrumbLongName(el.text)}
                </BreadcrumbButton>
              </span>
            </DynamicLink>
          </ControlledTooltip>
        </FluentBreadcrumbItem>
      </OverflowItem>
      {!isLastItem && <OverflowGroupDivider groupId={el.key} />}
    </Fragment>
  );
};

type TOverflowMenuProps = PartitionBreadcrumbItems<TRenderItemWithKey> & {
  onClick?: (key: number) => void;
};
const OverflowMenu = ({ overflowItems, startDisplayedItems, endDisplayedItems, onClick }: TOverflowMenuProps) => {
  const classes = useStyles();

  const { ref, isOverflowing, overflowCount } = useOverflowMenu<HTMLButtonElement>();

  if (!isOverflowing && (!overflowItems || overflowItems.length === 0)) return null;

  const overflowItemsCount = overflowItems ? overflowItems.length + overflowCount : overflowCount;

  return (
    <>
      <FluentBreadcrumbItem>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              id="menu"
              appearance="subtle"
              ref={ref}
              icon={<MoreHorizontal />}
              aria-label={`${overflowItemsCount} more items`}
              role="button"
              className={classes.menuButton}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {isOverflowing &&
                startDisplayedItems.map((item: TRenderItemWithKey) => (
                  <PathMenuItem id={item.key.toString()} item={item} key={item.key} onClick={onClick} />
                ))}
              {overflowItems &&
                overflowItems.map((item: TRenderItemWithKey) => (
                  <PathMenuItem id={item.key.toString()} item={item} key={item.key} onClick={onClick} />
                ))}
              {isOverflowing &&
                endDisplayedItems &&
                endDisplayedItems.map((item: TRenderItemWithKey) => (
                  <PathMenuItem id={item.key.toString()} item={item} key={item.key} onClick={onClick} />
                ))}
            </MenuList>
          </MenuPopover>
        </Menu>
      </FluentBreadcrumbItem>
      <BreadcrumbDivider className="mt-0.5" />
    </>
  );
};

type TProps = {
  items: TRenderItemWithKey[];
  hasCurrent: boolean;
  size?: 'small' | 'medium' | 'large';
  onClick?: (key: number) => void;
};

const Path = ({ items, hasCurrent, size = 'medium', onClick }: TProps) => {
  const styles = useStyles();

  const { startDisplayedItems, overflowItems, endDisplayedItems }: PartitionBreadcrumbItems<TRenderItemWithKey> =
    partitionBreadcrumbItems({
      items,
      maxDisplayedItems: 4,
    });

  return (
    <div className={styles.root}>
      <Overflow>
        <Breadcrumb size={size}>
          {startDisplayedItems.map((item: TRenderItemWithKey) => (
            <BreadcrumbItem
              key={item.id}
              size={size}
              el={item}
              isFirstItem={item.key === 0}
              isLastItem={item.key + 1 === items.length}
              hasCurrent={hasCurrent}
              onClick={onClick}
            />
          ))}
          <OverflowMenu
            overflowItems={overflowItems}
            startDisplayedItems={startDisplayedItems}
            endDisplayedItems={endDisplayedItems}
            onClick={onClick}
          />
          {endDisplayedItems &&
            endDisplayedItems.map((item: TRenderItemWithKey) => (
              <BreadcrumbItem
                key={item.id}
                size={size}
                el={item}
                isFirstItem={item.key === 0}
                isLastItem={item.key + 1 === items.length}
                hasCurrent={hasCurrent}
                onClick={onClick}
              />
            ))}
        </Breadcrumb>
      </Overflow>
    </div>
  );
};

export { Path };
